// FILE: frontend/src/services/quizApi.ts
import { API_BASE_URL } from "@/config";
import { auth } from "@/lib/firebase";

export interface QuizQuestion {
  question: string;
  options?: string[];
  answer: string;
  explanation?: string;
  rationales?: Record<string, string>;
  hook?: string;
}

type RenderMode = "mcq" | "flashcards" | "qa_only" | "qna";

type Req = {
  topic: string;
  num_questions: number;
  language_code?: string;
  difficulty?: string;
  explanation_mode?: string;
  need_distractor_reasons?: boolean;
  render_mode?: RenderMode;
  style?: string;
};

export async function generateQuiz(req: Req): Promise<QuizQuestion[]> {
  const user = auth?.currentUser;

  if (!user) {
    throw new Error("You must be signed in to generate quizzes");
  }

  let token: string;

  try {
    token = await user.getIdToken();
  } catch (err) {
    console.error("Failed to get auth token:", err);
    throw new Error("Authentication failed. Please sign in again.");
  }

  const renderMode = req.render_mode ?? "mcq";

  const response = await fetch(`${API_BASE_URL}/api/quiz/generate-quiz`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      topic: req.topic,
      num_questions: Math.max(
        1,
        Math.min(renderMode === "mcq" ? 20 : 50, req.num_questions || 5)
      ),
      language_code: req.language_code || "en-US",
      difficulty: req.difficulty || "moderate",
      explanation_mode: req.explanation_mode || "full",
      need_distractor_reasons:
        renderMode === "mcq" && req.need_distractor_reasons !== false,
      render_mode: renderMode,
      ...(req.style ? { style: req.style } : {}),
    }),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error("Authentication required. Please sign in and try again.");
    }

    if (response.status === 403) {
      const errorText = await response.text().catch(() => "Quota exceeded");

      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(
          errorJson.detail ||
            "You do not have enough credits to generate this video. Add credits and try again."
        );
      } catch {
        throw new Error(
          errorText ||
            "You do not have enough credits to generate this video. Add credits and try again."
        );
      }
    }

    if (response.status === 429) {
      const errorJson = await response.json().catch(() => null);
      const detail = errorJson?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message;

      throw new Error(
        message ||
          "Rate limit exceeded. Please wait a moment and try again."
      );
    }

    if (response.status === 400) {
      const errorText = await response.text().catch(() => "Invalid request");

      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(
          errorJson.detail ||
            errorJson.error ||
            "Invalid request. Please check your input."
        );
      } catch {
        throw new Error(
          errorText || "Invalid request. Please check your input."
        );
      }
    }

    const errorText = await response.text().catch(() => "quiz api error");
    throw new Error(`${response.status}: ${errorText}`);
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    console.error("Invalid API response format:", data);
    throw new Error("Invalid response format from quiz API");
  }

  if (data.length === 0) {
    throw new Error("No questions were generated. Try a different topic.");
  }

  return data as QuizQuestion[];
}
